// packages/ui/src/billing/UserPicker.tsx
//
// Shared admin-user picker — modal with debounced search by display name /
// email. Mirror of MerchantPicker / VendorPicker for any admin surface that
// needs to FK-pick an internal user (kế toán issuing credit-memos, support
// agent assignees, etc.). Replaces the "paste raw ObjectId into a text input"
// anti-pattern flagged by Cluster B audit (2026-05-23).
//
// Backend: GET /api/v1/admin/users?search=<q>&roleType=<role>&limit=20
//   Response envelope: { success, data: { users: [{ id, displayName, email,
//                       primaryRole, ... }], pagination }, requestId }
//   Handler: apps/api/src/modules/admin/handlers/adminUsers.handler.ts —
//            listAdminUsersHandler (Wave 6 Task 33).
//
// Usage:
//   <UserPicker
//     value={userId}
//     valueLabel={userName}
//     onChange={(id, label) => setUserId(id)}
//     roleType="accountant"
//   />

import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { apiClient } from '@printway/shared/api';

export interface UserPickerOption {
  id: string;
  /** Display name (or fallback to email/id when name is empty). */
  name: string;
  email?: string | null;
  primaryRole?: string | null;
}

export interface UserPickerProps {
  value: string | null;
  /** Caller-controlled label so the trigger shows the picked name even
   *  before the modal is opened. Pass null/undefined for empty. */
  valueLabel?: string | null;
  onChange: (id: string | null, label: string | null) => void;
  /** Optional BE-side role filter — e.g. `accountant`, `admin`, `support`. */
  roleType?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Forwarded to the trigger button for E2E test addressing. */
  testId?: string;
  /** Tailwind extension for the trigger button. */
  className?: string;
}

interface AdminUserListItem {
  id: string;
  displayName?: string | null;
  email?: string | null;
  primaryRole?: string | null;
}

interface AdminUserListResponse {
  users?: AdminUserListItem[];
  pagination?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

interface AdminUserListEnvelope {
  data?: AdminUserListResponse;
}

function unwrap(payload: unknown): AdminUserListResponse | null {
  if (!payload || typeof payload !== 'object') return null;
  const env = payload as AdminUserListEnvelope;
  if (env.data && typeof env.data === 'object') return env.data;
  return payload as AdminUserListResponse;
}

export function UserPicker({
  value,
  valueLabel,
  onChange,
  roleType,
  placeholder,
  disabled,
  testId,
  className,
}: UserPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<UserPickerOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Debounce 300ms — matches MerchantPicker cadence.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const reqIdRef = useRef(0);
  useEffect(() => {
    if (!open) return;
    const reqId = ++reqIdRef.current;
    const params = new URLSearchParams();
    params.set('limit', '20');
    if (roleType) params.set('roleType', roleType);
    if (debouncedSearch) params.set('search', debouncedSearch);
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const c = apiClient() as unknown as {
          GET: (
            p: string,
          ) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        };
        const { data, response } = await c.GET(
          `/api/v1/admin/users?${params.toString()}`,
        );
        if (reqId !== reqIdRef.current) return; // raced
        if (!response.ok) {
          setError(`HTTP ${response.status}`);
          setOptions([]);
          return;
        }
        const body = unwrap(data);
        const list = body?.users ?? [];
        setOptions(
          list.map((u) => ({
            id: u.id,
            name: u.displayName?.trim() || u.email?.trim() || u.id,
            email: u.email ?? null,
            primaryRole: u.primaryRole ?? null,
          })),
        );
      } catch (err) {
        if (reqId !== reqIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Network error');
        setOptions([]);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    })();
  }, [open, debouncedSearch, roleType]);

  const triggerLabel = useMemo(() => {
    if (value && valueLabel) return valueLabel;
    if (value) return value;
    return placeholder ?? 'Select user…';
  }, [value, valueLabel, placeholder]);

  function pick(opt: UserPickerOption): void {
    onChange(opt.id, opt.name);
    setOpen(false);
  }

  function clear(e: React.MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    onChange(null, null);
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        data-testid={testId ?? 'user-picker-trigger'}
        className={
          className ??
          'inline-flex h-10 w-full items-center justify-between gap-2 rounded-md border border-pw-border bg-white px-3 text-left text-sm text-pw-text hover:bg-pw-surface disabled:cursor-not-allowed disabled:opacity-50'
        }
      >
        <span
          className={
            value ? 'truncate' : 'truncate text-pw-text-muted'
          }
        >
          {triggerLabel}
        </span>
        <span className="flex items-center gap-1">
          {value ? (
            <button
              type="button"
              onClick={clear}
              className="text-xs text-pw-text-muted hover:text-pw-text"
              aria-label="Clear user"
            >
              ×
            </button>
          ) : null}
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-4 w-4 text-pw-text-muted"
          >
            <path
              d="M11 19a8 8 0 1 1 5.3-13.96l3.4-3.4 1.42 1.42-3.4 3.4A8 8 0 0 1 11 19zm0-2a6 6 0 1 0 0-12 6 6 0 0 0 0 12z"
              fill="currentColor"
            />
          </svg>
        </span>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Pick user"
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-6 sm:p-12"
          onClick={() => setOpen(false)}
        >
          <div
            role="document"
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl"
          >
            <div className="border-b border-pw-border px-4 py-3 text-sm font-semibold text-pw-text">
              Pick user {roleType ? `(role: ${roleType})` : ''}
            </div>
            <div className="border-b border-pw-border p-3">
              <input
                type="text"
                placeholder="Tìm theo tên hoặc email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                data-testid="user-picker-search"
                className="h-9 w-full rounded-md border border-pw-border bg-white px-3 text-sm text-pw-text outline-none focus:ring-2 focus:ring-pw-primary-strong/40"
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="px-4 py-6 text-center text-sm text-pw-text-muted">
                  Loading…
                </div>
              ) : error ? (
                <div className="px-4 py-6 text-center text-sm text-red-600">
                  {error}
                </div>
              ) : options.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-pw-text-muted">
                  {debouncedSearch
                    ? 'Không tìm thấy user phù hợp'
                    : 'Gõ để tìm user theo tên / email'}
                </div>
              ) : (
                <ul className="divide-y divide-pw-border">
                  {options.map((opt) => (
                    <li key={opt.id}>
                      <button
                        type="button"
                        onClick={() => pick(opt)}
                        data-testid={`user-picker-option-${opt.id}`}
                        className="flex w-full flex-col gap-0.5 px-4 py-2 text-left hover:bg-pw-surface"
                      >
                        <span className="text-sm font-medium text-pw-text">
                          {opt.name}
                        </span>
                        <span className="text-xs text-pw-text-muted">
                          {opt.email ? `${opt.email} · ` : ''}
                          {opt.primaryRole ? `${opt.primaryRole} · ` : ''}
                          {opt.id}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-end border-t border-pw-border px-4 py-3">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-9 items-center rounded-md border border-pw-border bg-white px-4 text-sm font-medium text-pw-text hover:bg-pw-surface"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
